import React, { useEffect, useState } from "react";
import { ReportCardSection } from "../../types";
import { markHeight } from "./reportCardModel";

interface Props {
  section: ReportCardSection;
  color: string;
  /** This criterion's share of its kind's weight, 0–1. */
  weightShare: number;
  /** Position in the kind, which staggers the entrance of the mark and its value. */
  index: number;
  /** The ground behind the mark, tinted per band by the rubric's own thresholds. */
  trackBackground: string;
  /** True for the card's lowest-graded criterion, which the gauge annotates. */
  flagged: boolean;
  /**
   * Bumped every time a slice sends the reader here. Zero means this row is not
   * the target; any other value replays the arrival flash, including a repeat
   * click on the same slice.
   */
  arrivalNonce: number;
}

/**
 * One criterion on the two-lane grid: heading, band and weight left, the gauge
 * on the shared axis right, the written feedback beneath the heading.
 */
const CriterionRow: React.FC<Props> = ({
  section,
  color,
  weightShare,
  index,
  trackBackground,
  flagged,
  arrivalNonce,
}) => {
  const [arrived, setArrived] = useState(false);

  useEffect(() => {
    if (!arrivalNonce) {
      setArrived(false);
      return;
    }
    // Drop the class and re-add it a frame later so the flash restarts even
    // when the reader clicks the same slice twice.
    setArrived(false);
    const frame = requestAnimationFrame(() => setArrived(true));
    return () => cancelAnimationFrame(frame);
  }, [arrivalNonce]);

  const grade = Math.min(100, Math.max(0, section.grade));
  const gauge = {
    "--g": grade,
    "--i": index,
  } as React.CSSProperties;

  return (
    <article
      className={`lane crit${arrived ? " is-target" : ""}`}
      id={`criterion-${section.criterionId}`}
    >
      <div className="crit-head">
        <h3>{section.name}</h3>
        <p className="tags">
          {section.bandName && (
            <span className="band" style={{ "--c": color } as React.CSSProperties}>
              {section.bandName}
            </span>
          )}
          <span className="weight">Weight {Math.round(weightShare * 100)}%</span>
        </p>
      </div>

      <div className="gauge">
        <div className="track" style={{ ...gauge, backgroundImage: trackBackground }}>
          {flagged && <span className="pin" style={gauge} />}
          <div
            className="mark"
            style={
              {
                ...gauge,
                "--h": `${markHeight(weightShare)}px`,
                "--c": color,
              } as React.CSSProperties
            }
          />
        </div>
        <div className={`readout${flagged ? " is-flagged" : ""}`} style={gauge}>
          <span>{Math.round(section.grade)}</span>
        </div>
      </div>

      <div className="crit-body">
        <p>{section.feedback}</p>
      </div>
    </article>
  );
};

export default CriterionRow;
